import React, { useState } from 'react';
import { Send, Paperclip, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_CATEGORIES = [
  'Compte', 'Authentification', 'Produits', 'Images', 'IA',
  'Publication', 'Paiement', 'Performance', 'Sécurité', 'Bug', 'Autre'
];

const SUPPORT_PRIORITIES = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  critical: 'Critique'
};

const SupportContact = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    priority: 'medium',
    subject: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category || !formData.subject || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/admin/beta/support/tickets`,
        {
          category: formData.category,
          priority: formData.priority,
          subject: formData.subject,
          description: formData.description,
          attachments: []
        },
        { withCredentials: true }
      );

      toast.success('Ticket créé avec succès');
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          category: '',
          priority: 'medium',
          subject: '',
          description: ''
        });
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      toast.error('Erreur lors de la création du ticket');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-8">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
              <h2 className="text-2xl font-bold text-green-900">Ticket créé avec succès !</h2>
              <p className="text-center text-green-700">
                Notre équipe a bien reçu votre demande et vous répondra dans les plus brefs délais.
              </p>
              <p className="text-sm text-green-600">
                Vous pouvez suivre l'état de votre ticket dans votre espace personnel.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Contacter le Support</h1>
        <p className="text-muted-foreground mt-2">
          Besoin d'aide ? Créez un ticket de support et notre équipe vous répondra rapidement.
        </p>
      </div>

      {/* Support Form */}
      <Card>
        <CardHeader>
          <CardTitle>Nouveau ticket de support</CardTitle>
          <CardDescription>
            Veuillez décrire votre problème ou question en détail
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category & Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Sélectionnez une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priorité *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  required
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SUPPORT_PRIORITIES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet *</Label>
              <Input
                id="subject"
                placeholder="Résumé court de votre demande"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                minLength={5}
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description détaillée *</Label>
              <Textarea
                id="description"
                placeholder="Décrivez votre problème ou question en détail...&#10;&#10;Informations utiles :&#10;- Que s'est-il passé ?&#10;- Quand cela s'est-il produit ?&#10;- Quelles actions avez-vous effectuées ?&#10;- Quel était le résultat attendu ?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                minLength={10}
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Plus vous fournissez de détails, plus nous pourrons vous aider rapidement.
              </p>
            </div>

            {/* Info box */}
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                💡 Conseil
              </h3>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Avant de soumettre un ticket, consultez notre{' '}
                <a href="/help" className="underline font-medium">
                  centre d'aide
                </a>
                {' '}et la{' '}
                <a href="/help" className="underline font-medium">
                  FAQ
                </a>
                . Vous y trouverez peut-être une réponse immédiate à votre question.
              </p>
            </div>

            {/* Submit */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFormData({
                    category: '',
                    priority: 'medium',
                    subject: '',
                    description: ''
                  });
                }}
              >
                Réinitialiser
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer le ticket
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* User tickets link */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Suivre mes tickets</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Consultez l'état de vos demandes précédentes
              </p>
            </div>
            <Button variant="outline" asChild>
              <a href="/support/my-tickets">
                Mes tickets
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportContact;
